import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";

const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Cookie Policy | Savi Coliving</title>
        <meta name="description" content="Cookie policy for Savi Coliving. Learn about the cookies we use and how to manage your preferences." />
        <link rel="canonical" href="https://www.savicoliving.com/cookies" />
      </Helmet>

      <PublicNav />

      <div className="pt-24 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-2">Cookie Policy</h1>
          <p className="text-[#2E2A26]/40 text-sm mb-10">Last updated: April 2026</p>

          <div className="prose prose-stone max-w-none space-y-8">
            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">What Are Cookies?</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
                Cookies are small text files stored on your device when you visit a website. They help the site remember your preferences and understand how you interact with it. Some cookies are essential for the site to work, while others help us improve your experience.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">Cookies We Use</h2>
              
              <div className="space-y-4">
                <div className="bg-[#F4ECE2] rounded-xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[#2E2A26] text-sm font-medium">Essential Cookies</p>
                    <span className="text-[#C85A38] text-xs font-medium bg-[#C85A38]/10 px-2 py-0.5 rounded-full">Always active</span>
                  </div>
                  <p className="text-[#2E2A26]/60 text-xs leading-relaxed mb-3">
                    Required for the website to function. These cannot be disabled.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-[#2E2A26]/60">
                      <thead><tr className="border-b border-stone-200"><th className="text-left py-1.5 pr-4 font-medium text-[#2E2A26]/80">Cookie</th><th className="text-left py-1.5 pr-4 font-medium text-[#2E2A26]/80">Purpose</th><th className="text-left py-1.5 font-medium text-[#2E2A26]/80">Duration</th></tr></thead>
                      <tbody>
                        <tr className="border-b border-stone-100"><td className="py-1.5 pr-4">savi_cookie_consent</td><td className="py-1.5 pr-4">Stores your cookie preferences</td><td className="py-1.5">1 year</td></tr>
                        <tr><td className="py-1.5 pr-4">savi_auth_token</td><td className="py-1.5 pr-4">Guest authentication session</td><td className="py-1.5">Session</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-[#F4ECE2] rounded-xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[#2E2A26] text-sm font-medium">Analytics Cookies</p>
                    <span className="text-[#2E2A26]/40 text-xs font-medium bg-[#2E2A26]/5 px-2 py-0.5 rounded-full">Requires consent</span>
                  </div>
                  <p className="text-[#2E2A26]/60 text-xs leading-relaxed mb-3">
                    Help us understand how visitors use the site. All data is aggregated and anonymous.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-[#2E2A26]/60">
                      <thead><tr className="border-b border-stone-200"><th className="text-left py-1.5 pr-4 font-medium text-[#2E2A26]/80">Cookie</th><th className="text-left py-1.5 pr-4 font-medium text-[#2E2A26]/80">Provider</th><th className="text-left py-1.5 pr-4 font-medium text-[#2E2A26]/80">Purpose</th><th className="text-left py-1.5 font-medium text-[#2E2A26]/80">Duration</th></tr></thead>
                      <tbody>
                        <tr className="border-b border-stone-100"><td className="py-1.5 pr-4">_ga, _ga_*</td><td className="py-1.5 pr-4">Google Analytics</td><td className="py-1.5 pr-4">Page views, user behaviour</td><td className="py-1.5">2 years</td></tr>
                        <tr><td className="py-1.5 pr-4">ph_*</td><td className="py-1.5 pr-4">PostHog</td><td className="py-1.5 pr-4">Product analytics, session replay</td><td className="py-1.5">1 year</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-[#F4ECE2] rounded-xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[#2E2A26] text-sm font-medium">Marketing Cookies</p>
                    <span className="text-[#2E2A26]/40 text-xs font-medium bg-[#2E2A26]/5 px-2 py-0.5 rounded-full">Requires consent</span>
                  </div>
                  <p className="text-[#2E2A26]/60 text-xs leading-relaxed">
                    Used for retargeting and ad conversion tracking. We do not currently use marketing cookies, but this category is available if we add them in the future.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">Managing Your Preferences</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-3">
                You can manage your cookie preferences at any time:
              </p>
              <ul className="list-disc pl-5 text-[#2E2A26]/60 text-sm space-y-1.5">
                <li>Use the cookie banner when you first visit our site</li>
                <li>Clear your browser cookies to reset your preferences — the banner will appear again on your next visit</li>
                <li>Adjust cookie settings in your browser (Chrome, Firefox, Safari, etc.)</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">Your Rights</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
                Under the GDPR and Spanish data protection law (LOPDGDD), you have the right to withdraw consent at any time, request access to your data, and request its deletion. For more details, see our <Link to="/privacy" className="text-[#C85A38] hover:underline">Privacy Policy</Link>.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">Contact</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
                Questions about our cookie usage? Contact us at <a href="mailto:hola@savicoliving.com" className="text-[#C85A38]">hola@savicoliving.com</a>.
              </p>
            </section>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default CookiePolicyPage;
